/**
 * EditGroup — admin-only form to change a group's cover, avatar, name,
 * description and privacy (mobile editGroup parity, create-group layout).
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import {
  CameraIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  PostImageIcon,
} from '../components/ui/icons';
import { ListStatus } from '../components/ui/ListStatus';
import { GroupPrivacyModal } from '../features/groups/GroupPrivacyModal';
import { GroupDetailsSkeleton } from '../features/groups/GroupSkeletons';
import { useEditGroupForm } from '../features/groups/useEditGroupForm';
import type { GroupImageSlot } from '../features/groups/useGroupImagePicker';
import '../features/groups/groups.css';

export function EditGroup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const {
    loading,
    loadError,
    reload,
    canEdit,
    groupName,
    description,
    privacy,
    coverPreview,
    avatarPreview,
    errors,
    saving,
    setGroupName,
    setDescription,
    setPrivacy,
    setImage,
    submit,
    cancel,
  } = useEditGroupForm(groupId);

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeSlot = useRef<GroupImageSlot>('cover');

  // Only admins may edit, so a member who deep-links here goes back to the group.
  useEffect(() => {
    if (canEdit === false && groupId) navigate(`/groups/${groupId}`, { replace: true });
  }, [canEdit, groupId, navigate]);

  const openPicker = (slot: GroupImageSlot) => {
    activeSlot.current = slot;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) setImage(activeSlot.current, file);
  };

  const privacyLabel = privacy
    ? t(`home.createGroupForm.${privacy.toLowerCase()}`)
    : t('home.createGroupForm.choosePrivacy');

  const renderBody = () => {
    if (loadError) {
      return (
        <ListStatus variant="error" message={loadError} onAction={reload} busy={loading} />
      );
    }
    // Covers both the initial fetch and the frame before a non-admin is bounced.
    if (loading || canEdit !== true) return <GroupDetailsSkeleton />;

    return (
      <form
        className="create-group__form"
        onSubmit={event => { event.preventDefault(); submit().catch(() => undefined); }}
      >
        <div className="create-group__media">
          <button
            type="button"
            className="create-group__cover"
            onClick={() => openPicker('cover')}
            aria-label={t('home.createGroupForm.coverPhoto')}
          >
            {coverPreview ? (
              <img className="create-group__cover-image" src={coverPreview} alt="" aria-hidden />
            ) : (
              <span className="create-group__cover-placeholder" aria-hidden>
                <PostImageIcon size={56} />
              </span>
            )}
            <span className="create-group__cover-badge" aria-hidden><CameraIcon size={18} /></span>
          </button>
          <button
            type="button"
            className="create-group__avatar"
            onClick={() => openPicker('avatar')}
            aria-label={t('home.createGroupForm.profilePhoto')}
          >
            {avatarPreview && (
              <img className="create-group__avatar-image" src={avatarPreview} alt="" aria-hidden />
            )}
            <span className="create-group__avatar-badge" aria-hidden><CameraIcon size={14} /></span>
          </button>
          <input
            ref={fileInputRef}
            className="create-group__file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileChange}
          />
        </div>

        <div className="create-group__field">
          <label className="create-group__label" htmlFor="edit-group-name">
            {t('groupDetails.editGroup.groupName')}
          </label>
          <input
            id="edit-group-name"
            className={`create-group__input${errors.groupName ? ' create-group__input--error' : ''}`}
            value={groupName}
            placeholder={t('home.createGroupForm.groupNamePlaceholder')}
            aria-invalid={Boolean(errors.groupName)}
            aria-describedby={errors.groupName ? 'edit-group-name-error' : undefined}
            onChange={event => setGroupName(event.target.value)}
          />
          <FormError id="edit-group-name-error" message={errors.groupName} />
        </div>

        <div className="create-group__field">
          <label className="create-group__label" htmlFor="edit-group-description">
            {t('groupDetails.editGroup.description')}
          </label>
          <textarea
            id="edit-group-description"
            className={`create-group__textarea${errors.description ? ' create-group__textarea--error' : ''}`}
            value={description}
            placeholder={t('home.createGroupForm.descriptionPlaceholder')}
            aria-invalid={Boolean(errors.description)}
            aria-describedby={errors.description ? 'edit-group-description-error' : undefined}
            onChange={event => setDescription(event.target.value)}
          />
          <FormError id="edit-group-description-error" message={errors.description} />
        </div>

        <div className="create-group__field">
          <span className="create-group__label" id="edit-group-privacy-label">
            {t('groupDetails.editGroup.privacy')}
          </span>
          <button
            type="button"
            className={`create-group__dropdown${privacy ? '' : ' create-group__dropdown--placeholder'}${errors.privacy ? ' create-group__dropdown--error' : ''}`}
            onClick={() => setPrivacyOpen(true)}
            aria-labelledby="edit-group-privacy-label"
            aria-haspopup="dialog"
            aria-expanded={privacyOpen}
          >
            {privacyLabel}
            <ChevronDownIcon size={20} />
          </button>
          <FormError message={errors.privacy} />
        </div>

        <div className="create-group__actions">
          <Button type="submit" size="lg" fullWidth disabled={saving}>
            {t(saving ? 'common.buttons.saving' : 'groupDetails.editGroup.saveChanges')}
          </Button>
          <Button type="button" size="lg" variant="ghost" fullWidth onClick={cancel}>
            {t('common.buttons.cancel')}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <section className="create-group" aria-labelledby="edit-group-title">
      <header className="create-group__header">
        <button type="button" className="create-group__back" onClick={() => navigate(-1)}>
          <ChevronLeftIcon size={20} />
          <span id="edit-group-title">{t('groupDetails.editGroup.title')}</span>
        </button>
      </header>

      {renderBody()}

      {privacyOpen && (
        <GroupPrivacyModal
          selected={privacy}
          onClose={() => setPrivacyOpen(false)}
          onSelect={setPrivacy}
        />
      )}
    </section>
  );
}
